import { createSignal } from "solid-js";
import { AsyncEventEmitter } from "@vladfrangu/async_event_emitter";
import { JSONParse, JSONStringify } from "json-with-bigint";
/**
 * All possible event client states.
 */
export var ConnectionState;
(function (ConnectionState) {
    ConnectionState[ConnectionState["Idle"] = 0] = "Idle";
    ConnectionState[ConnectionState["Connecting"] = 1] = "Connecting";
    ConnectionState[ConnectionState["Connected"] = 2] = "Connected";
    ConnectionState[ConnectionState["Disconnected"] = 3] = "Disconnected";
})(ConnectionState || (ConnectionState = {}));
/**
 * Simple wrapper around the Revolt websocket service.
 */
export class EventClient extends AsyncEventEmitter {
    options;
    #protocolVersion;
    #transportFormat;
    ping;
    #setPing;
    state;
    #setStateSetter;
    #socket;
    #heartbeatIntervalReference;
    #pongTimeoutReference;
    #connectTimeoutReference;
    #lastError;
    /**
     * Create a new event client.
     * @param protocolVersion Target protocol version
     * @param transportFormat Communication format
     * @param options Configuration options
     */
    constructor(protocolVersion, transportFormat = "json", options) {
        super();
        this.#protocolVersion = protocolVersion;
        this.#transportFormat = transportFormat;
        this.options = {
            heartbeatInterval: 30,
            pongTimeout: 10,
            connectTimeout: 10,
            debug: false,
            ...options,
        };
        const [state, setState] = createSignal(ConnectionState.Idle);
        this.state = state;
        this.#setStateSetter = setState;
        const [ping, setPing] = createSignal(-1);
        this.ping = ping;
        this.#setPing = setPing;
        this.disconnect = this.disconnect.bind(this);
    }
    /**
     * Set the current state
     * @param state state
     */
    setState(state) {
        this.#setStateSetter(state);
        this.emit("state", state);
    }
    /**
     * Connect to the websocket service.
     * @param uri WebSocket URI
     * @param token Authentication token
     */
    connect(uri, token) {
        this.disconnect();
        this.#lastError = undefined;
        this.setState(ConnectionState.Connecting);
        this.#connectTimeoutReference = setTimeout(() => this.disconnect(), this.options.pongTimeout * 1e3);
        const url = new URL(uri);
        url.searchParams.set("version", this.#protocolVersion.toString());
        url.searchParams.set("format", this.#transportFormat);
        url.searchParams.set("token", token);
        // todo: pass-through ts as a configuration option
        // todo: then remove /settings/fetch from web client
        // todo: do the same for unreads
        // url.searchParams.append("ready", "users");
        // url.searchParams.append("ready", "servers");
        // url.searchParams.append("ready", "channels");
        // url.searchParams.append("ready", "members");
        // url.searchParams.append("ready", "emojis");
        // url.searchParams.append("ready", "voice_states");
        // url.searchParams.append("ready", "user_settings[ordering]");
        // url.searchParams.append("ready", "user_settings[notifications]");
        // url.searchParams.append("ready", "unreads or something");
        // url.searchParams.append("ready", "policy_changes");
        this.#socket = new WebSocket(url);
        this.#socket.onopen = () => {
            this.#heartbeatIntervalReference = setInterval(() => {
                this.send({ type: "Ping", data: +new Date() });
                this.#pongTimeoutReference = setTimeout(() => this.disconnect(), this.options.pongTimeout * 1e3);
            }, this.options.heartbeatInterval * 1e3);
        };
        this.#socket.onerror = (error) => {
            this.#lastError = { type: "socket", data: error };
            this.emit("error", error);
        };
        this.#socket.onmessage = (event) => {
            clearInterval(this.#connectTimeoutReference);
            if (this.#transportFormat === "json") {
                if (typeof event.data === "string") {
                    this.handle(JSONParse(event.data));
                }
            }
        };
        let closed = false;
        const thisSocket = this.#socket;
        this.#socket.onclose = () => {
            if (closed)
                return;
            closed = true;
            // PeyPhone: a stale socket closing late must not tear down a newer connection
            if (this.#socket && this.#socket !== thisSocket)
                return;
            this.#socket = undefined;
            this.setState(ConnectionState.Disconnected);
            this.disconnect();
        };
    }
    /**
     * Disconnect the websocket client.
     */
    disconnect() {
        if (!this.#socket)
            return;
        clearInterval(this.#heartbeatIntervalReference);
        clearInterval(this.#connectTimeoutReference);
        clearInterval(this.#pongTimeoutReference);
        const socket = this.#socket;
        this.#socket = undefined;
        socket.close();
    }
    /**
     * Send an event to the server.
     * @param event Event
     */
    send(event) {
        if (this.options.debug)
            console.debug("[C->S]", event);
        if (!this.#socket)
            throw "Socket closed, trying to send.";
        this.#socket.send(JSONStringify(event));
    }
    /**
     * Handle events intended for client before passing them along.
     * @param event Event
     */
    handle(event) {
        if (this.options.debug)
            console.debug("[S->C]", event);
        switch (event.type) {
            case "Ping":
                this.send({
                    type: "Pong",
                    data: event.data,
                });
                return;
            case "Pong":
                clearTimeout(this.#pongTimeoutReference);
                this.#setPing(+new Date() - event.data);
                if (this.options.debug)
                    console.debug(`[ping] ${this.ping()}ms`);
                return;
            case "Error":
                this.#lastError = {
                    type: "revolt",
                    data: event.data,
                };
                this.emit("error", event);
                this.disconnect();
                return;
        }
        switch (this.state()) {
            case ConnectionState.Connecting:
                if (event.type === "Authenticated") {
                    // no-op
                }
                else if (event.type === "Ready") {
                    this.emit("event", event);
                    this.setState(ConnectionState.Connected);
                }
                else {
                    throw `Unreachable code. Received ${event.type} in Connecting state.`;
                }
                break;
            case ConnectionState.Connected:
                if (event.type === "Authenticated" || event.type === "Ready") {
                    throw `Unreachable code. Received ${event.type} in Connected state.`;
                }
                else {
                    this.emit("event", event);
                }
                break;
            default:
                throw `Unreachable code. Received ${event.type} in state ${this.state()}.`;
        }
    }
    /**
     * Last error encountered by events client
     */
    get lastError() {
        return this.#lastError;
    }
}
